/**
 * @fileoverview Associated Companies component for displaying a person's company affiliations
 * @active Component used in active person profile pages
 *
 * This component displays a table of companies associated with a person, including
 * their roles, titles, and company information.
 *
 * @see {@link src/components/personBy_Id/overview/Overview.jsx}
 * @see {@link src/components/ui/tables/Table.jsx}
 * @see {@link src/types/company.ts}
 * @see {@link src/types/address.ts}
 */

"use client";

import React, { useState } from "react";

import { Flex, Icon, Img, Text, Tooltip } from "@chakra-ui/react";
import Link from "next/link";

import LinkedInSvg from "@/assets/svg/linkedIn.svg";
import OutSvg from "@/assets/svg/out-blank-icon.svg";
import ShareSvg from "@/assets/svg/share-icon.svg";
import WebsiteSvg from "@/assets/svg/website1.svg";
import { BlueTextBoxArrow } from "@/components/ui/box/BlueTextBoxArrow";
import { urlChecker } from "@/components/ui/buttons/ExternalUrlIcons.server";
import DefaultImage from "@/components/ui/DefaultImage";
import { h_400_14_135_28 } from "@/styles/fontStyles";
import { addressSelector } from "@/utils/addressSelector";
import { imgSelector } from "@/utils/imgSelector";
import type { AddressColorResult } from "@/types/address";

import { EayTabPeople, RenderTabPeople } from "../../../ui/tables/Table";
import { PersonOverviewWrapper } from "../Overview";

import { AssociatedCompaniesModal } from "./AssociatedCompaniesModal";

/**
 * Mapping of entity types to their API endpoint paths
 */
const endpointName: Record<string, string> = {
  Company: "companies",
  "Investment Firm": "investors/firms/",
  "": "investors/funds/",
};

/**
 * Props for the AssociatedCompanies component
 */
interface AssociatedCompaniesProps {
  /** Person data containing company associations */
  onePerson: {
    resJunctions?: Array<{
      id: string;
      resEntity?: {
        nameBrand?: string;
        slug?: string;
        typeRecord?: string;
        webUrl?: {
          urlLinkedin?: string;
          urlWebsite?: string;
        };
        addresses?: any[];
      };
      corporateTitle?: {
        personCompanyTitle?: string;
        personEntityJobLevel?: string;
        personEntityFunction?: string;
      };
    }>;
  };
}

/**
 * Props for the CompanyElement component
 */
interface CompanyElementProps {
  /** Company name */
  nameBrand: string;
  /** Company logo/image URL */
  img: string;
  /** Company profile link */
  link: any;
  /** Image type for rendering */
  imgType?: "person";
  /** Whether to show blur effect */
  isBlurred?: boolean;
}

/**
 * Props for the PlainTextInTable component
 */
interface PlainTextInTableProps {
  /** Text content to display */
  data?: string | AddressColorResult;
  /** Custom text styles */
  styleText?: Record<string, any>;
  /** Whether to show null value */
  isNull?: boolean;
}

/**
 * Props for the LinkedInWebSiteElement component
 */
interface LinkedInWebSiteElementProps {
  /** URL to link to */
  url: string;
  /** Type of link (linkedIn or webSite) */
  type: "linkedIn" | "webSite";
}

/**
 * Props for the blockSwitch function
 */
interface BlockSwitchProps {
  /** Current table column item */
  item: { title: string };
  /** Current row data */
  mainItem: any;
  /** Full table data */
  data: any[];
}

/**
 * AssociatedCompanies component displays a table of companies associated with a person
 */
function AssociatedCompanies({ onePerson }: AssociatedCompaniesProps) {
  const [isVisibleEay, setIsVisibleEay] = useState(false);
  const fixedTab = [{ title: "Company" }];
  const variableTab = [
    { title: "HQ" },
    { title: "Website", icon: OutSvg },
    { title: "Title" },
    { title: "Level" },
    { title: "Function" },
  ];

  const newData = onePerson?.resJunctions
    ? [...onePerson.resJunctions].sort((a, b) => {
        const nameA = a?.resEntity?.nameBrand || "";
        const nameB = b?.resEntity?.nameBrand || "";
        return nameA > nameB ? 1 : -1;
      })
    : [];

  const findLastUniqueCompany = newData.reduce(
    (acc, item, index) => {
      if (item?.resEntity?.nameBrand) {
        acc[item.resEntity.nameBrand] = index;
      }
      return acc;
    },
    {} as Record<string, number>
  );

  const lastCompanyIndexArr = Object.values(findLastUniqueCompany);

  return (
    <>
      {onePerson?.resJunctions?.length ? (
        <PersonOverviewWrapper
          customStyleInner={{
            maxW: { base: "100%", md: "100%", xl: "1124px" },
          }}
        >
          <BlueTextBoxArrow
            title="Associated Companies"
            heading="h3"
            headingStyles={{}}
            boxStyles={{}}
          />

          <Flex
            className="hide-scrollbar"
            px={"20px"}
            mt={"20px"}
            pos={"relative"}
          >
            <RenderTabPeople
              {...{
                mask: fixedTab,
                data: newData,
                tabContainerStyle: { w: { base: "160px", md: "fit-content" } },
                setIsVisibleEay,
                blockSwitch,
                isFundInvestments: false,
                isShadow: false,
                lastPersonIndexArr: lastCompanyIndexArr,
                isPeoplePage: true,
              }}
            />
            <RenderTabPeople
              {...{
                mask: variableTab,
                data: newData,
                setIsVisibleEay,
                blockSwitch,
                isFundInvestments: false,
                isShadow: false,
                tabContainerStyle: {
                  flex: "1",
                  overflow: "hidden",
                  overflowX: "scroll",
                },
                lastPersonIndexArr: lastCompanyIndexArr,
                isPeoplePage: true,
              }}
            />
            {isVisibleEay && (
              <EayTabPeople
                {...{
                  data: onePerson?.resJunctions,
                  ModalComponent: AssociatedCompaniesModal,
                  tabContainerStyle: {},
                  lastPersonIndexArr: lastCompanyIndexArr,
                }}
              />
            )}
          </Flex>
        </PersonOverviewWrapper>
      ) : null}
    </>
  );
}

/**
 * Switch function to render different cell content based on column type
 */
function blockSwitch({
  item,
  mainItem,
  data,
}: BlockSwitchProps): JSX.Element | null {
  const firstCompany = data?.find(
    (prsn) => prsn?.resEntity?.nameBrand === mainItem?.resEntity?.nameBrand
  );

  switch (item?.title) {
    case "Company": {
      const nameBrand = mainItem?.resEntity?.nameBrand || "";
      const img = imgSelector({ data: mainItem?.resEntity }) || "";
      const link = mainItem?.resEntity?.slug
        ? `/${endpointName[mainItem?.resEntity?.typeRecord || ""]}/${
            mainItem?.resEntity?.slug
          }`
        : "#";
      return (
        <>
          {mainItem?.id === firstCompany?.id ? (
            <CompanyElement nameBrand={nameBrand} img={img} link={link} />
          ) : null}
        </>
      );
    }
    case "Website": {
      const linkedInUrl = urlChecker(mainItem?.resEntity?.webUrl?.urlLinkedin);
      const webUrl = urlChecker(mainItem?.resEntity?.webUrl?.urlWebsite);
      return (
        <>
          {mainItem?.id === firstCompany?.id ? (
            <Flex
              align={"center"}
              w={"100%"}
              justify={"center"}
              columnGap={"8px"}
            >
              {webUrl ? (
                <Link
                  href={webUrl}
                  target="_blank"
                  rel={"noopener noreferrer nofollow"}
                >
                  <Icon as={ShareSvg} w={"16px"} h={"16px"} />
                </Link>
              ) : null}
              {linkedInUrl ? (
                <Link
                  href={linkedInUrl}
                  target="_blank"
                  rel={"noopener noreferrer nofollow"}
                >
                  <Icon as={LinkedInSvg} w={"20px"} h={"20px"} />
                </Link>
              ) : null}
            </Flex>
          ) : null}
        </>
      );
    }
    case "HQ": {
      const data = addressSelector({
        data: mainItem?.resEntity?.addresses,
      });
      return (
        <>
          {mainItem?.id === firstCompany?.id ? (
            <PlainTextInTable data={data} styleText={{}} isNull={false} />
          ) : null}
        </>
      );
    }
    case "Title": {
      const data = mainItem?.corporateTitle?.personCompanyTitle;
      return <PlainTextInTable data={data} styleText={{}} isNull={false} />;
    }
    case "Level": {
      const data = mainItem?.corporateTitle?.personEntityJobLevel;
      return <PlainTextInTable data={data} styleText={{}} isNull={false} />;
    }
    case "Function": {
      const data = mainItem?.corporateTitle?.personEntityFunction;
      return <PlainTextInTable data={data} styleText={{}} isNull={false} />;
    }
    default:
      return null;
  }
}

/**
 * Component to display plain text in a table cell
 */
export function PlainTextInTable({
  data,
  styleText = {},
  isNull = false,
}: PlainTextInTableProps) {
  let res: string | null = null;
  if (data) {
    res = typeof data === "string" ? data : JSON.stringify(data);
  } else if (!isNull) {
    res = "—";
  }
  return (
    <Text
      as={"h3"}
      {...{
        h: "26px",
        w: "full",
        alignSelf: "center",
        pt: "4px",
        ...h_400_14_135_28,
        ...styleText,
      }}
      isTruncated
    >
      {res}
    </Text>
  );
}

/**
 * Component to display LinkedIn or website icons with links
 */
export function LinkedInWebSiteElement({
  url,
  type,
}: LinkedInWebSiteElementProps) {
  return url ? (
    <Link href={url} target="_blank" rel={"noopener noreferrer nofollow"}>
      {type === "linkedIn" ? (
        <Icon as={LinkedInSvg} w={"20px"} h={"20px"} />
      ) : null}
      {type === "webSite" ? (
        <Icon as={WebsiteSvg} w={"20px"} h={"20px"} />
      ) : null}
    </Link>
  ) : (
    <PlainTextInTable data={undefined} styleText={{}} isNull={false} />
  );
}

/**
 * Component to display company information with logo and name
 */
export function CompanyElement({
  nameBrand,
  img,
  imgType,
  link,
  isBlurred = false,
}: CompanyElementProps) {
  return (
    <Link
      href={link}
      style={{
        display: "flex",
        alignContent: "center",
        gap: "4px",
        width: "100%",
        maxWidth: "100%",
        position: "relative",
      }}
    >
      <Tooltip
        label={nameBrand || ""}
        placement="top"
        pt={"4px"}
        shouldWrapChildren
      >
        <DefaultImage
          src={img}
          type={imgType}
          options={{
            blockSize: "24px",
            w: "24px",
            borderRadius: "2px",
            alt: nameBrand || "",
            objectFit: "scale-down",
          }}
        />
      </Tooltip>
      <PlainTextInTable data={nameBrand} styleText={{}} isNull={false} />
      {isBlurred && nameBrand?.length > 27 ? (
        <Flex
          {...{
            position: "absolute",
            w: "40px",
            h: "26px",
            top: "0px",
            right: "-10px",
          }}
        >
          <Img
            src="/images/other/white-gradient.png"
            alt="gradient"
            className=" w-[30px] h-11"
          />
        </Flex>
      ) : null}
    </Link>
  );
}

export default AssociatedCompanies;
